import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, mergeMap, take } from 'rxjs/operators';

import { $scanTag, SystemTags } from '../../../../shared/constants/scan-tag';
import { IScanTag, IScanTagServerResponse } from '../../../../shared/interfaces/scan-tag.interface';
import { UserService } from '../../services/user.service';
import { ScanTagService } from '../../services/scan-tag.service';
import { LoadErrorHandler } from '../../services/load-error-handler';
import { AutomatedScanAdvancedService } from '../../pages/public/automated-scan/advanced/automated-scan-advanced.service';
import { IDigitalPropertyListItem } from '../../../../shared/interfaces/digital-property.interface';
import { $digitalProperty } from '../../../../shared/constants/digital-properties';
import { $workspace } from '../../../../shared/constants/workspace';
import { ScanTagUtility } from '../../../../shared/utils/scan-tag.utility';
import { scanningType } from '../../../../shared/constants/scanning';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
@Component({
  selector: 'app-scan-tag-selector',
  standalone: false,
  templateUrl: './scan-tag-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScanTagSelectorComponent),
      multi: true,
    },
  ],
})
export class ScanTagSelectorComponent implements OnInit, ControlValueAccessor {
  public readonly $scanTag: typeof $scanTag = $scanTag;

  private allTags: IScanTag[] = [];
  private userTags: IScanTag[] = [];
  private enableReservedSystemTags: boolean;

  @Input() public disabled: boolean;
  @Input() public disabledUnselectedValue: boolean;
  @Input() public required: boolean;
  @Input() public errorState: boolean;
  @Input() public errorMessage: string;

  public subscriptions: Subscription;
  public selectedTagId: string | null;

  private _onChange: (_scanId: string) => void;
  private _onTouched: () => void;

  constructor(
    private automatedScanAdvancedService: AutomatedScanAdvancedService,
    private changeDetector: ChangeDetectorRef,
    private loadErrorHandler: LoadErrorHandler,
    private scanTagService: ScanTagService,
    private userService: UserService,
  ) {
    this.subscriptions = new Subscription();
    this._onChange = (_scanId: string): void => {};
    this._onTouched = (): void => {};
    this.selectedTagId = null;
    this.disabledUnselectedValue = true;
    this.disabled = false;
  }

  public get availableTags(): IScanTag[] {
    return this.enableReservedSystemTags ? this.allTags : this.userTags;
  }

  private subscribeToScanningType(): void {
    this.subscriptions.add(
      this.automatedScanAdvancedService.getScanningType$().subscribe((type: scanningType) => {
        if (type === scanningType.automatedUserFlow) {
          this.disabled = true;
          this.enableReservedSystemTags = true;
          this.selectByName(SystemTags.AutomatedUserFlow);
        } else {
          this.disabled = false;
          this.enableReservedSystemTags = false;
          this.resetSelectedTagIfUnavailable();
        }
        this.changeDetector.detectChanges();
      }),
    );
  }

  private handleSelectTag(_scanTagId: string): void {
    if (_scanTagId === this.selectedTagId) {
      return;
    }
    this.selectedTagId = _scanTagId;
  }

  public onScanTagChange(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.handleSelectTag(target.value);
    this._onTouched();
    this._onChange(target.value);
    this.changeDetector.detectChanges();
  }

  private selectByName(tagName: string): void {
    const scanTag: IScanTag = this.availableTags.find((tag: IScanTag) => tag[$scanTag.tagName] === tagName);

    if (SharedCommonUtility.notNullish(scanTag)) {
      this.handleSelectTag(scanTag._id);
    }
  }

  private resetSelectedTagIfUnavailable(): void {
    if (this.availableTags.every((tag: IScanTag) => tag._id !== this.selectedTagId)) {
      this.handleSelectTag(null);
    }
  }

  public registerOnChange(fn: (_level: string) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetector.detectChanges();
  }

  public writeValue(value: string): void {
    this.selectedTagId = value;
    this.changeDetector.detectChanges();
  }

  private onDigitalPropertyChanged(digitalProperty: IDigitalPropertyListItem): void {
    const digitalPropertyId: string = digitalProperty[$digitalProperty._id];
    const workspaceId: string = digitalProperty[$digitalProperty.workspace][$workspace._id];
    this.subscriptions.add(
      this.scanTagService.getScanTagsFromDigitalProperty(workspaceId, digitalPropertyId).subscribe({
        next: (response: IScanTagServerResponse): void => {
          this.allTags = response.tags;
          this.userTags = response.tags.filter((tag: IScanTag) => !ScanTagUtility.isReservedSystemTag(tag[$scanTag.tagName]));
          this.changeDetector.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.loadErrorHandler.handleError('error_getting_scan_tags', error, ScanTagSelectorComponent.name);
        },
      }),
    );
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.userService.currentDigitalProperty$
        .pipe(
          distinctUntilChanged(),
          mergeMap((): Observable<IDigitalPropertyListItem | null> => this.userService.currentDigitalProperty$.pipe(take(1))),
        )
        .subscribe(this.onDigitalPropertyChanged.bind(this)),
    );

    this.subscribeToScanningType();
  }
}
